import React from 'react';
import PublicLayout from '../../components/layout/PublicLayout';

const TermsOfServicePage: React.FC = () => {
  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">Terms of Service</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8">Last updated: January 19, 2026</p>

        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              By accessing or using Litrlly ("the Service"), you agree to be bound by these Terms of Service.
              If you do not agree to these terms, please do not use our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">2. Description of Service</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Litrlly is an AI-powered pronunciation practice platform that helps users improve their English
              speaking skills through real-time feedback, phoneme analysis, and personalized coaching.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">3. User Accounts</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">To use certain features of the Service, you must:</p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 mb-4 space-y-2">
              <li>Create an account with accurate and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Promptly notify us of any unauthorized use of your account</li>
              <li>Be at least 13 years of age</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              You are responsible for all activities that occur under your account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">4. Subscription Plans and Payments</h2>
            <h3 className="text-xl font-medium text-gray-800 dark:text-gray-200 mb-3">Free Tier</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              The free tier provides limited access with 3 pronunciation recordings per day.
            </p>

            <h3 className="text-xl font-medium text-gray-800 dark:text-gray-200 mb-3">Paid Subscriptions</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Paid subscriptions (Basic, Pro, Pro+Interview) provide additional features as described on our
              pricing page. By subscribing, you agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 mb-4 space-y-2">
              <li>Pay the applicable subscription fees</li>
              <li>Automatic renewal unless cancelled before the renewal date</li>
              <li>Provide accurate payment information</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 dark:text-gray-200 mb-3">Cancellation and Refunds</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              You may cancel your subscription at any time through your account settings. Cancellation takes
              effect at the end of your current billing period. We do not provide refunds for partial billing periods.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">5. Acceptable Use</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 mb-4 space-y-2">
              <li>Use the Service for any unlawful purpose</li>
              <li>Upload harmful, offensive, or inappropriate content</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Use automated systems to access the Service without permission</li>
              <li>Share your account with others or resell access</li>
              <li>Reverse engineer or attempt to extract the source code</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">6. Intellectual Property</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              The Service and its original content, features, and functionality are owned by Litrlly and are
              protected by international copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              You retain ownership of any content you submit (such as voice recordings), but grant us a license
              to process this content to provide our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">7. AI-Generated Feedback</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Our Service uses artificial intelligence to provide pronunciation feedback and coaching.
              While we strive for accuracy, AI-generated feedback should be used as a learning tool and
              not as a definitive assessment of language proficiency. Results may vary and are not guaranteed.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">8. Disclaimer of Warranties</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
              FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We do not warrant that the Service will be uninterrupted, error-free, or completely secure.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, LITRLLY SHALL NOT BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES,
              WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER
              INTANGIBLE LOSSES.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">10. Termination</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We may terminate or suspend your account and access to the Service immediately, without prior
              notice, for conduct that we believe violates these Terms or is harmful to other users, us, or
              third parties, or for any other reason at our sole discretion.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">11. Changes to Terms</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We reserve the right to modify these Terms at any time. We will provide notice of significant
              changes by posting the new Terms on this page and updating the "Last updated" date. Your
              continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">12. Governing Law</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction
              in which Litrlly operates, without regard to its conflict of law provisions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">13. Contact Us</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              Email: <a href="mailto:support@litrlly.app" className="text-blue-600 dark:text-blue-400 hover:underline">support@litrlly.app</a>
            </p>
          </section>
        </div>
      </div>
    </PublicLayout>
  );
};

export default TermsOfServicePage;
